import React, { useEffect, useMemo, useRef, useState } from 'react';
import CloseIcon from '../../images/CloseIcon';
import CloseIconV2 from '../../images/CloseIconV2';
import {
  getGroupProduct,
  getQuickAddDefaults,
  quickAddProduct,
} from '../../services/items.services';
import { getListAttributes } from '../../services/attributes.service';
import { showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';

/**
 * Quick Add Product modal — back-office shortcut to create a sellable
 * product from just the five user-facing fields (name, price, group,
 * attributes, barcode). The server fills SKU, type, tax group, base
 * UOM, cost, price type and validity window from country-scoped
 * defaults in dbo.country_quick_add_defaults.
 *
 * Visual language follows the existing Duplicate-item modal in
 * Items.tsx (same backdrop, panel size, button radii, color palette).
 * All user-visible strings flow through `language?.[key] ?? fallback`
 * to match the rest of the back-office i18n convention.
 *
 * The Quick Add button on Items.tsx checks the same defaults endpoint
 * and disables itself when no country defaults exist; this modal does
 * a second defensive check at open-time and surfaces a clear message
 * inside the panel if anything has changed since the button check.
 */

interface QuickAddDefaults {
  countryId: string;
  defaultTaxGroupId: string;
  defaultBaseUomId: string;
  defaultBaseUomName: string | null;
  defaultProductType: number;
  defaultPriceType: number;
  defaultCost: number;
}

interface ProductGroupRow {
  id: string;
  groupName: string;
}

interface AttributeRow {
  id: string;
  attributeName: string;
}

interface QuickAddItemDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  language: any;
}

const QuickAddItemDialog: React.FC<QuickAddItemDialogProps> = ({
  isOpen,
  onClose,
  onCreated,
  language,
}) => {
  const userId = localStorage.getItem('userId') || '';
  const companyId = localStorage.getItem('companyId') || '';

  const t = (key: string, fallback: string): string =>
    (language && language?.[key]) || fallback;

  // Defaults panel + availability state
  const [defaults, setDefaults] = useState<QuickAddDefaults | null>(null);
  const [defaultsLoading, setDefaultsLoading] = useState(false);
  const [defaultsError, setDefaultsError] = useState<string | null>(null);

  // Catalog state for group + attribute pickers
  const [groups, setGroups] = useState<ProductGroupRow[]>([]);
  const [attributes, setAttributes] = useState<AttributeRow[]>([]);

  // Form state
  const [formValues, setFormValues] = useState<{
    productName: string;
    price: string;
    productGroupId: string;
    barcode: string;
  }>({
    productName: '',
    price: '',
    productGroupId: '',
    barcode: '',
  });
  const [selectedAttributes, setSelectedAttributes] = useState<AttributeRow[]>([]);
  const [attributeSearch, setAttributeSearch] = useState('');
  const [attrDropdownOpen, setAttrDropdownOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const attrDropdownRef = useRef<HTMLDivElement | null>(null);

  // Close attribute dropdown on outside click
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (
        attrDropdownRef.current &&
        !attrDropdownRef.current.contains(e.target as Node)
      ) {
        setAttrDropdownOpen(false);
      }
    };
    if (attrDropdownOpen) {
      document.addEventListener('mousedown', onClickOutside);
    }
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [attrDropdownOpen]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setFormValues({ productName: '', price: '', productGroupId: '', barcode: '' });
      setSelectedAttributes([]);
      setAttributeSearch('');
      setAttrDropdownOpen(false);
      setErrors({});
      setSubmitting(false);
    }
  }, [isOpen]);

  // Load defaults + catalogs on open
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const load = async () => {
      setDefaultsLoading(true);
      setDefaultsError(null);

      try {
        const [defaultsRes, groupsRes, attributesRes] = await Promise.allSettled([
          getQuickAddDefaults(),
          getGroupProduct(companyId, 1, 1000, ''),
          getListAttributes(companyId, 1, 200, ''),
        ]);

        if (cancelled) return;

        // Defaults — fatal if missing
        if (defaultsRes.status === 'fulfilled') {
          const payload: any = defaultsRes.value?.data?.data;
          if (payload) {
            setDefaults(payload);
          } else {
            setDefaultsError(
              t(
                'Quick Add is not configured for this country',
                'Quick Add is not configured for this country',
              ),
            );
          }
        } else {
          const status = defaultsRes.reason?.response?.status;
          setDefaultsError(
            status === 404
              ? t(
                  'Quick Add is not configured for this country',
                  'Quick Add is not configured for this country',
                )
              : t('Failed to load defaults', 'Failed to load defaults'),
          );
        }

        // Groups — soft failure (user can still see error inline).
        //
        // The product-groups list endpoint returns its rows under
        // `productGroupWithCompany` at the *top level* of the response
        // body (not nested under another `data` envelope), so we read
        // from `groupsRes.value.data.productGroupWithCompany`. Defensive
        // fallbacks cover the wrapped-envelope variant if the API ever
        // standardises.
        if (groupsRes.status === 'fulfilled') {
          const root: any = groupsRes.value?.data ?? {};
          const list: any[] =
            root.productGroupWithCompany ??
            root.data?.productGroupWithCompany ??
            (Array.isArray(root.data) ? root.data : []) ??
            [];
          setGroups(
            list.map((g: any) => ({ id: g.id, groupName: g.groupName ?? '' })),
          );
        } else {
          setGroups([]);
        }

        // Attributes — soft failure.
        //
        // The attributes list endpoint *does* use the wrapped envelope
        // (`{ status, data: { attributes: [...] } }`) — matches the
        // pattern in CreateItems.tsx which reads `data.data.attributes`.
        if (attributesRes.status === 'fulfilled') {
          const root: any = attributesRes.value?.data ?? {};
          const list: any[] =
            root.data?.attributes ??
            root.attributes ??
            (Array.isArray(root.data) ? root.data : []) ??
            [];
          setAttributes(
            list.map((a: any) => ({
              id: a.id ?? a.attributeId,
              attributeName: a.attributeName ?? '',
            })),
          );
        } else {
          setAttributes([]);
        }
      } finally {
        if (!cancelled) setDefaultsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const filteredAttributes = useMemo(() => {
    const term = attributeSearch.trim().toLowerCase();
    return attributes
      .filter((a) => !selectedAttributes.some((s) => s.id === a.id))
      .filter((a) =>
        term ? (a.attributeName || '').toLowerCase().includes(term) : true,
      )
      .slice(0, 50);
  }, [attributes, selectedAttributes, attributeSearch]);

  const selectedGroupName = useMemo(() => {
    return (
      groups.find((g) => g.id === formValues.productGroupId)?.groupName ?? ''
    );
  }, [groups, formValues.productGroupId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    }
  };

  const handleAddAttribute = (attr: AttributeRow) => {
    setSelectedAttributes((prev) =>
      prev.some((p) => p.id === attr.id) ? prev : [...prev, attr],
    );
    setAttributeSearch('');
    // Close the dropdown after a pick: the list is absolutely positioned and
    // otherwise covers the selected-attribute chips, so the user can't see the
    // selection landed.
    setAttrDropdownOpen(false);
  };

  const handleRemoveAttribute = (id: string) => {
    setSelectedAttributes((prev) => prev.filter((a) => a.id !== id));
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!formValues.productName.trim()) {
      next.productName = t('Product Name is required', 'Product Name is required');
    }
    const priceNum = Number(formValues.price);
    if (formValues.price === '' || Number.isNaN(priceNum) || priceNum < 0) {
      next.price = t('Price is required', 'Price is required');
    }
    if (!formValues.productGroupId) {
      next.productGroupId = t('Group is required', 'Group is required');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!validate()) return;
    if (!defaults) return;

    setSubmitting(true);
    try {
      const payload = {
        productName: formValues.productName.trim(),
        price: Number(formValues.price),
        productGroupId: formValues.productGroupId,
        attributeIds: selectedAttributes.map((a) => a.id),
        barcode: formValues.barcode.trim() || undefined,
        userId,
      };
      const res: any = await quickAddProduct(payload);
      if (res?.data?.status === 200) {
        showToast({
          type: 'success',
          message: t('Item Created !', 'Item Created !'),
        });
        onCreated();
        onClose();
      } else {
        const msg =
          res?.data?.message ?? t('Failed to create item', 'Failed to create item');
        showToast({ type: 'error', message: msg });
      }
    } catch (err: any) {
      showApiError(err, {
        language,
        titleFallback: t('Failed to create item', 'Failed to create item'),
        setFieldErrors: (fe) => setErrors((prev) => ({ ...prev, ...fe })),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isBlocked = !defaults || !!defaultsError;

  return (
    <div
      className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      } transition-opacity duration-300 ease-in-out`}
      style={{
        backgroundColor: '#22252552',
        zIndex: 200,
        transition: 'opacity 0.3s ease, visibility 0.3s ease',
        width: '100vw',
        height: '100vh',
        left: 0,
        top: 0,
      }}
    >
      <div
        className={`transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-y-0' : 'translate-y-4 opacity-0'
        }`}
        style={{
          padding: '24px',
          borderRadius: '24px',
          backgroundColor: '#FFFFFF',
          width: '530px',
          maxHeight: '90vh',
          overflowY: 'auto',
          transition: 'transform 0.3s ease, opacity 0.3s ease',
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <p
              style={{ fontSize: '20px', color: '#222525' }}
              className="font-semibold font-inter leading-[24px]"
            >
              {t('Quick add product', 'Quick add product')}
            </p>
            <p
              style={{ fontSize: '14px', color: '#6A7878', marginTop: '12px' }}
              className="font-inter leading-[24px]"
            >
              {t(
                'Add a product with just the essentials',
                'Add a product with just the essentials',
              )}
            </p>
          </div>
          <div
            onClick={() => {
              if (submitting) return;
              onClose();
            }}
            className="flex items-center justify-center cursor-pointer"
            style={{ width: '24px', height: '24px' }}
          >
            <CloseIcon />
          </div>
        </div>

        {/* Blocked state — no defaults for country */}
        {defaultsError && !defaultsLoading && (
          <div
            style={{
              marginTop: '16px',
              padding: '12px 16px',
              borderRadius: '12px',
              backgroundColor: '#FFF4F4',
              border: '1px solid #F4C7C7',
              color: '#8B1A1F',
              fontSize: '14px',
            }}
            className="font-inter"
          >
            {defaultsError}
          </div>
        )}

        {/* Body */}
        <div style={{ marginTop: '12px' }}>
          {/* Item Name */}
          <div className="space-y-2" style={{ margin: '20px 0px' }}>
            <div className="flex items-center">
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: '#4A5353' }}
              >
                {t('Item Name', 'Item Name')}
              </p>
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: '#BA0E13' }}
              >
                {t('*', '*')}
              </p>
            </div>
            <input
              tabIndex={1}
              type="text"
              name="productName"
              value={formValues.productName}
              onChange={handleChange}
              placeholder={t('Enter product name', 'Enter product name')}
              disabled={isBlocked || submitting}
              className="text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none focus-none bg-transparent w-full disabled:opacity-50"
            />
            {errors.productName && <p className="error">{errors.productName}</p>}
          </div>

          {/* Price */}
          <div className="space-y-2" style={{ margin: '20px 0px' }}>
            <div className="flex items-center">
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: '#4A5353' }}
              >
                {t('Price', 'Price')}
              </p>
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: '#BA0E13' }}
              >
                {t('*', '*')}
              </p>
            </div>
            <input
              tabIndex={2}
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              name="price"
              value={formValues.price}
              onChange={handleChange}
              placeholder={t('Enter price', 'Enter price')}
              disabled={isBlocked || submitting}
              className="text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none focus-none bg-transparent w-full disabled:opacity-50"
            />
            {errors.price && <p className="error">{errors.price}</p>}
          </div>

          {/* Group */}
          <div className="space-y-2" style={{ margin: '20px 0px' }}>
            <div className="flex items-center">
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: '#4A5353' }}
              >
                {t('Group', 'Group')}
              </p>
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: '#BA0E13' }}
              >
                {t('*', '*')}
              </p>
            </div>
            <select
              tabIndex={3}
              name="productGroupId"
              value={formValues.productGroupId}
              onChange={handleChange}
              disabled={isBlocked || submitting || groups.length === 0}
              className="text-[14px] font-inter text-input border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none focus-none bg-transparent w-full disabled:opacity-50"
              style={{ color: formValues.productGroupId ? '#222525' : '#9CA3AF' }}
            >
              <option value="" disabled>
                {groups.length === 0
                  ? t(
                      'No groups available — create a group first',
                      'No groups available — create a group first',
                    )
                  : t('Select group', 'Select group')}
              </option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.groupName}
                </option>
              ))}
            </select>
            {errors.productGroupId && (
              <p className="error">{errors.productGroupId}</p>
            )}
          </div>

          {/* Attributes (optional, multi-select with search) */}
          <div className="space-y-2" style={{ margin: '20px 0px' }} ref={attrDropdownRef}>
            <p
              className="text-[14px] font-inter leading-[20px] font-medium"
              style={{ color: '#4A5353' }}
            >
              {t('Attributes', 'Attributes')}
            </p>
            <div className="relative w-full">
              <input
                tabIndex={4}
                type="text"
                value={attributeSearch}
                onChange={(e) => {
                  setAttributeSearch(e.target.value);
                  setAttrDropdownOpen(true);
                }}
                onFocus={() => setAttrDropdownOpen(true)}
                placeholder={t(
                  'Type to search attributes…',
                  'Type to search attributes…',
                )}
                disabled={isBlocked || submitting}
                className="text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none focus-none bg-transparent w-full disabled:opacity-50"
              />
              {attrDropdownOpen && filteredAttributes.length > 0 && (
                <div
                  className="absolute left-0 right-0 z-10 mt-1 bg-white border border-[#DBE4E4] rounded-xl overflow-hidden"
                  style={{ maxHeight: '220px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                >
                  {filteredAttributes.map((a) => (
                    <div
                      key={a.id}
                      onClick={() => handleAddAttribute(a)}
                      className="px-3 py-2 cursor-pointer hover:bg-[#F8F8F8] text-[14px] font-inter"
                      style={{ color: '#222525' }}
                    >
                      {a.attributeName}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedAttributes.length > 0 && (
              <div
                className="flex items-center"
                style={{
                  gap: '8px',
                  width: '100%',
                  marginTop: '12px',
                  flexWrap: 'wrap',
                }}
              >
                {selectedAttributes.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center w-fit"
                    style={{
                      borderRadius: '30px',
                      padding: '8px 16px',
                      backgroundColor: '#EBF0F0',
                      gap: '12px',
                    }}
                  >
                    <span
                      className="font-inter"
                      style={{ fontSize: '14px', fontWeight: 500, color: '#4A5353' }}
                    >
                      {a.attributeName}
                    </span>
                    <div
                      className="cursor-pointer"
                      onClick={() => handleRemoveAttribute(a.id)}
                    >
                      <CloseIconV2 />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Barcode */}
          <div className="space-y-2" style={{ margin: '20px 0px' }}>
            <p
              className="text-[14px] font-inter leading-[20px] font-medium"
              style={{ color: '#4A5353' }}
            >
              {t('Barcodes', 'Barcode')}
            </p>
            <input
              tabIndex={5}
              type="text"
              name="barcode"
              value={formValues.barcode}
              onChange={handleChange}
              placeholder={t('Enter barcode', 'Enter barcode')}
              disabled={isBlocked || submitting}
              className="text-[14px] font-inter placeholder:text-placeholder text-input border border-[#DBE4E4] px-2 py-3 rounded-xl outline-none focus-none bg-transparent w-full disabled:opacity-50"
            />
            {errors.barcode && <p className="error">{errors.barcode}</p>}
          </div>

          {/* Auto-fill summary panel */}
          {defaults && !defaultsError && (
            <div
              style={{
                padding: '16px',
                borderRadius: '12px',
                backgroundColor: '#F8F8F8',
                border: '1px solid #DBE4E4',
                marginTop: '8px',
              }}
            >
              <p
                style={{ fontSize: '13px', fontWeight: 500, color: '#4A5353' }}
                className="font-inter"
              >
                {t('Auto-filled details', 'Auto-filled details')}
              </p>
              <p
                style={{
                  fontSize: '12px',
                  color: '#6A7878',
                  marginTop: '6px',
                  lineHeight: '18px',
                }}
                className="font-inter"
              >
                {t(
                  'This product will be created with default type, tax, cost and unit. You can edit these later from the full product page.',
                  'This product will be created with default type, tax, cost and unit. You can edit these later from the full product page.',
                )}
              </p>
              <div
                className="flex items-center"
                style={{ gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}
              >
                {defaults.defaultBaseUomName && (
                  <span
                    style={{
                      fontSize: '12px',
                      padding: '4px 10px',
                      borderRadius: '20px',
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #DBE4E4',
                      color: '#4A5353',
                    }}
                    className="font-inter"
                  >
                    {t('Unit', 'Unit')}: {defaults.defaultBaseUomName}
                  </span>
                )}
                <span
                  style={{
                    fontSize: '12px',
                    padding: '4px 10px',
                    borderRadius: '20px',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #DBE4E4',
                    color: '#4A5353',
                  }}
                  className="font-inter"
                >
                  {t('Cost', 'Cost')}: {Number(defaults.defaultCost).toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end"
          style={{ marginTop: '24px', gap: '12px' }}
        >
          <div
            onClick={() => {
              if (submitting) return;
              onClose();
            }}
            className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
            style={{
              borderRadius: '40px',
              padding: '10px 24px',
              border: '1px solid #C9D7D8',
              color: '#222525',
              opacity: submitting ? 0.5 : 1,
              pointerEvents: submitting ? 'none' : 'auto',
            }}
          >
            {t('Cancel', 'Cancel')}
          </div>
          <div
            onClick={() => {
              if (submitting || isBlocked) return;
              handleSubmit();
            }}
            className="text-[14px] leading-[22px] font-inter font-medium"
            style={{
              borderRadius: '40px',
              padding: '10px 24px',
              backgroundColor: '#0F7679',
              color: '#FFFFFF',
              opacity: submitting || isBlocked ? 0.5 : 1,
              cursor: submitting || isBlocked ? 'not-allowed' : 'pointer',
              pointerEvents: submitting || isBlocked ? 'none' : 'auto',
            }}
          >
            {submitting
              ? t('Adding...', 'Adding...')
              : t('Add product', 'Add product')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickAddItemDialog;
